import React from 'react';
import { Card, Icon, Button, Empty } from 'antd';
import { useNavigate } from "react-router-dom";
import useDomainAnalysisProcessPolling from '../process/hooks/useDomainAnalysisProcessPolling';
import { useFetchOnPageSeoDomainChecksQuery } from './service';
import { DPA_DASHBOARD_POLLING_TIME_MS } from '../settings';
import { FailedCheck } from './types';
import ImpactIndicator from '../shared/ImpactIndicator';
import SiteWidePassedChecks from './SiteWidePassedChecks';
import CardTitleWithProgressIndicator from '../shared/CardTitleWithProgressIndicator';
import useBatchUrlAnalysisProcessesPolling from '../process/hooks/useBatchUrlAnalysisProcessesPolling';

function getDomainFailedChecksListData(failedChecks: FailedCheck[]) {
    return failedChecks.map((failedCheck) => ({
        testKey: failedCheck.test_key,
        lastFound: failedCheck.last_found,
        title: failedCheck.title,
        recommendation: failedCheck.recommendation,
        relevance: failedCheck.relevance,
        impactScore: failedCheck.relevance,
    }));
}

type Props = {
    websiteGuid: string;
};

export default function SiteWideChecks({ websiteGuid }: Props) {
    const navigate = useNavigate();

    const { isRunningDomainAnalysis } = useDomainAnalysisProcessPolling(websiteGuid);
    const { isRunningBatchAnalysis } = useBatchUrlAnalysisProcessesPolling(websiteGuid);
    const { data: domainChecks, isLoading: isLoadingDomainChecks } = useFetchOnPageSeoDomainChecksQuery(
        { w_guid: websiteGuid },
        {
            // The polling interval is in milliseconds!
            pollingInterval: isRunningDomainAnalysis ? DPA_DASHBOARD_POLLING_TIME_MS : 0,
            // The polling interval is reset if we call the hook again, refetchOnMount
            // Can take a number that will refetch if the cache value is older than the value
            // This is in seconds!
            refetchOnMountOrArgChange: isRunningDomainAnalysis ? DPA_DASHBOARD_POLLING_TIME_MS / 1000 : false,
        }
    );

    const onViewSeoTestDetails = (seoTestKey: string) => {
        navigate(`/deep-analysis/report/?w_guid=${websiteGuid}#${seoTestKey}`);
    };

    const failedChecks = domainChecks && domainChecks.failed ? getDomainFailedChecksListData(domainChecks.failed) : [];
    const sortedFailedChecks = [...failedChecks].sort((a, b) => b.impactScore - a.impactScore);

    const passedChecks = domainChecks && domainChecks.passed ? domainChecks.passed : [];
    const sortedPassedChecks = [...passedChecks].sort((a, b) => b.relevance - a.relevance);
    //const lastFoundDate = failedChecks && failedChecks.length > 0 ? [...failedChecks].sort((a, b) => b.lastFound.localeCompare(a.lastFound))[0].lastFound : null;

    return (
        <Card
            className="deep-analysis-failed-checks site-wide"
            loading={isLoadingDomainChecks}
            title={
                <CardTitleWithProgressIndicator
                    title="Site-wide SEO checks"
                    description="SEO audit in progress"
                    isInProgress={isRunningBatchAnalysis}
                />
            }
        >
            <Card className="failed-checks-list" type="inner" title="Failed checks">
                {sortedFailedChecks.length === 0 && <Empty description={isRunningDomainAnalysis ? 'No failed checks yet' : 'No failed checks'} />}
                {sortedFailedChecks.length > 0 && sortedFailedChecks.map((failedCheck) => (
                    <div className="failed-check-item" key={failedCheck.testKey}>
                        <ImpactIndicator severity={failedCheck.impactScore} />
                        <div className="item-description">
                            <span>{failedCheck.title}</span>
                            <span
                                className="item-description-recommendation"
                                dangerouslySetInnerHTML={{ __html: failedCheck.recommendation }}
                            />
                        </div>
                        <Button
                            className="details-button website"
                            size="small"
                            onClick={() => onViewSeoTestDetails(failedCheck.testKey)}
                        >
                            <Icon type="eye" /> View Details
                        </Button>
                    </div>
                ))}
            </Card>
            <SiteWidePassedChecks
                passedChecks={sortedPassedChecks}
                isRunningBatchAnalysis={isRunningDomainAnalysis}
                onViewSeoTestDetails={onViewSeoTestDetails}
            />
        </Card>
    );
}
